import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runDatasetQuery } from "@/lib/reporting/engine.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Truck, DollarSign, Clock, Package, Undo2 } from "lucide-react";
import { useMemo } from "react";
import { useReportsRange } from "@/hooks/use-reports-range";

export const Route = createFileRoute("/_authenticated/app/reports/shipping")({ component: ShippingDash });

function ShippingDash() {
  const { tenantId } = useScope();
  const run = useServerFn(runDatasetQuery);
  const { sinceIso: since, untilIso: until, days: windowDays } = useReportsRange(30);
  const rangeFilter = (column: string) =>
    until
      ? { type: "and" as const, conditions: [
          { type: "condition" as const, column, op: "gte" as const, value: since },
          { type: "condition" as const, column, op: "lte" as const, value: until },
        ] }
      : { type: "condition" as const, column, op: "gte" as const, value: since };

  const { data: byCarrier, isLoading } = useQuery({
    queryKey: ["rpt-ship-carrier", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "shipments",
      filters: rangeFilter("shipped_at"),
      group: ["carrier"],
      aggregations: [
        { column: "id", fn: "count", alias: "shipments" },
        { column: "rate_cents", fn: "sum", alias: "spend" },
      ],
      limit: 50, offset: 0,
    }}),
  });

  const { data: returnLabels, isLoading: rlLoading } = useQuery({
    queryKey: ["rpt-return-labels", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "return_labels",
      filters: {
        type: "and", conditions: [
          { type: "condition", column: "purchased_at", op: "gte", value: since },
          ...(until ? [{ type: "condition" as const, column: "purchased_at", op: "lte" as const, value: until }] : []),
          { type: "condition", column: "status", op: "neq", value: "voided" },
        ],
      },
      group: ["carrier"],
      aggregations: [
        { column: "id", fn: "count", alias: "labels" },
        { column: "rate_cents", fn: "sum", alias: "spend" },
      ],
      limit: 50, offset: 0,
    }}),
  });

  const totals = useMemo(() => {
    const rows = byCarrier?.rows ?? [];
    let s = 0, sp = 0, w = 0;
    for (const r of rows) { s += Number(r.shipments ?? 0); sp += Number(r.spend ?? 0); w += Number(r.weight ?? 0); }
    return { shipments: s, spend: sp, avgCost: s > 0 ? sp / s : 0, weight: Math.round(w) };
  }, [byCarrier]);

  const returnTotals = useMemo(() => {
    const rows = returnLabels?.rows ?? [];
    let n = 0, sp = 0;
    for (const r of rows) { n += Number(r.labels ?? 0); sp += Number(r.spend ?? 0); }
    return { labels: n, spend: sp };
  }, [returnLabels]);
  const returnCarrierData = (returnLabels?.rows ?? [])
    .map((r: any) => ({
      carrier: r.carrier ?? "—",
      labels: Number(r.labels ?? 0),
      spend: +(Number(r.spend ?? 0) / 100).toFixed(2),
    }))
    .sort((a, b) => b.labels - a.labels);

  const carrierData = (byCarrier?.rows ?? [])
    .map((r: any) => ({
      carrier: r.carrier ?? "—",
      shipments: Number(r.shipments ?? 0),
      spend: +(Number(r.spend ?? 0) / 100).toFixed(2),
    }))
    .sort((a, b) => b.shipments - a.shipments);

  return (
    <>
      <PageHeader title="Shipping" description={`Carrier mix, spend, and volume over the selected window (${windowDays} day${windowDays === 1 ? "" : "s"}).`} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Shipments" value={totals.shipments} icon={<Truck className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Total spend" value={totals.spend} format="currency" icon={<DollarSign className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Avg cost / shipment" value={totals.avgCost} format="currency" icon={<Clock className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Weight (oz)" value={totals.weight} icon={<Package className="h-4 w-4" />} loading={isLoading} />
        </div>
        <ChartCard title="Carrier Performance">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={carrierData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="carrier" fontSize={11} />
              <YAxis yAxisId="l" fontSize={11} />
              <YAxis yAxisId="r" orientation="right" fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="l" dataKey="shipments" fill="var(--primary)" name="Shipments" />
              <Bar yAxisId="r" dataKey="spend" fill="var(--muted-foreground)" name="Spend ($)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Return labels" value={returnTotals.labels} icon={<Undo2 className="h-4 w-4" />} loading={rlLoading} />
          <KpiCard label="Return label spend" value={returnTotals.spend / 100} format="currency" icon={<DollarSign className="h-4 w-4" />} loading={rlLoading} />
        </div>
        <ChartCard title="Return Labels by Carrier">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={returnCarrierData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="carrier" fontSize={11} />
              <YAxis yAxisId="l" fontSize={11} />
              <YAxis yAxisId="r" orientation="right" fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="l" dataKey="labels" fill="var(--primary)" name="Labels" />
              <Bar yAxisId="r" dataKey="spend" fill="var(--muted-foreground)" name="Spend ($)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}